'use client'

// Interface de base pour les arrière-plans
export interface BackgroundRenderer {
  drawBackground(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number): void
  drawGround(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number): void
}

// Fonctions utilitaires de base pour le dessin
export class BaseRenderer {

  // Dessiner un nuage
  static drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, size: number = 1): void {
    ctx.save()
    ctx.fillStyle = '#FFFFFF'

    ctx.fillRect(x, y + 5 * size, 30 * size, 10 * size)
    ctx.fillRect(x + 5 * size, y, 20 * size, 20 * size)
    ctx.fillRect(x + 10 * size, y + 3 * size, 25 * size, 12 * size)

    ctx.restore()
  }

  // Dessiner un petit nuage
  static drawSmallCloud(ctx: CanvasRenderingContext2D, x: number, y: number, opacity: number = 0.8): void {
    ctx.save()
    ctx.fillStyle = `rgba(255,255,255,${opacity})`

    ctx.fillRect(x, y + 3, 20, 6)
    ctx.fillRect(x + 3, y, 14, 12)
    ctx.fillRect(x + 6, y + 2, 16, 8)

    ctx.restore()
  }

  // Dessiner un arbre générique
  static drawTree(ctx: CanvasRenderingContext2D, x: number, y: number, trunkColor: string = '#8B4513', foliageColor: string = '#228B22'): void {
    ctx.save()

    // Tronc
    ctx.fillStyle = trunkColor
    ctx.fillRect(x + 8, y + 20, 8, 40)

    // Feuillage
    ctx.fillStyle = foliageColor
    ctx.fillRect(x, y, 24, 30)
    ctx.fillRect(x + 3, y - 3, 18, 36)
    ctx.fillRect(x + 6, y - 6, 12, 42)

    ctx.restore()
  }

  // Dessiner un soleil
  static drawSun(ctx: CanvasRenderingContext2D, x: number, y: number, size: number = 1): void {
    ctx.save()

    // Corps du soleil
    ctx.fillStyle = '#FFD700'
    ctx.beginPath()
    ctx.arc(x, y, 20 * size, 0, 2 * Math.PI)
    ctx.fill()

    // Rayons
    ctx.strokeStyle = '#FFD700'
    ctx.lineWidth = 3 * size
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4
      ctx.beginPath()
      ctx.moveTo(x + Math.cos(angle) * 25 * size, y + Math.sin(angle) * 25 * size)
      ctx.lineTo(x + Math.cos(angle) * 35 * size, y + Math.sin(angle) * 35 * size)
      ctx.stroke()
    }

    ctx.restore()
  }



  // Dessiner un building générique
  static drawBuilding(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, color: string, windowColor: string = '#4169E1'): void {
    ctx.save()

    // Façade principale
    ctx.fillStyle = color
    ctx.fillRect(x, y, width, height)

    // Fenêtres
    ctx.fillStyle = windowColor
    const windowRows = Math.floor(height / 20)
    const windowCols = Math.floor(width / 15)

    for (let row = 0; row < windowRows; row++) {
      for (let col = 0; col < windowCols; col++) {
        if (col < windowCols - 1 && row < windowRows - 1) { // Éviter les bords
          ctx.fillRect(x + 5 + col * 15, y + 10 + row * 20, 6, 8)
        }
      }
    }

    ctx.restore()
  }
}

// Classe pour gérer les environnements répétitifs
export class EnvironmentRenderer {

  // Dessiner des éléments répétitifs dans un environnement
  static drawRepeatingElements(
    ctx: CanvasRenderingContext2D,
    cameraX: number,
    canvasWidth: number,
    elements: Array<{
      spacing: number,
      offset: number,
      drawFunction: (ctx: CanvasRenderingContext2D, x: number) => void
    }>
  ): void {
    const startX = cameraX - 300
    const endX = cameraX + canvasWidth + 300

    elements.forEach(element => {
      const { spacing, offset, drawFunction } = element

      for (let x = Math.floor(startX / spacing) * spacing; x <= endX; x += spacing) {
        drawFunction(ctx, x + offset)
      }
    })
  }

  // Dessiner un ciel avec dégradé
  static drawSkyGradient(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number,
                        topColor: string = '#87CEEB', bottomColor: string = '#B0E0E6'): void {
    const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight)
    gradient.addColorStop(0, topColor)
    gradient.addColorStop(1, bottomColor)

    ctx.fillStyle = gradient
    if (cameraX !== 0) {
      ctx.fillRect(cameraX - 100, 0, canvasWidth + 200, canvasHeight)
    } else {
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)
    }
  }

  // Dessiner un sol avec texture
  static drawTexturedGround(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number,
                           groundY: number, groundHeight: number, baseColor: string,
                           patternColor: string, patternSize: number = 8): void {
    const startX = cameraX - 100
    const endX = cameraX + canvasWidth + 100

    // Sol de base
    ctx.fillStyle = baseColor
    ctx.fillRect(startX, groundY, endX - startX, groundHeight)

    // Motif de texture
    ctx.fillStyle = patternColor
    for (let x = Math.floor(startX / patternSize) * patternSize; x <= endX; x += patternSize) {
      for (let y = groundY + 2; y < groundY + groundHeight; y += patternSize) {
        if ((Math.floor(x / patternSize) + Math.floor(y / patternSize)) % 2 === 0) {
          ctx.fillRect(x, y, patternSize - 2, patternSize - 2)
        }
      }
    }

    // Bordure supérieure
    ctx.fillStyle = '#A9A9A9'
    ctx.fillRect(startX, groundY - 2, endX - startX, 2)
  }
}
